import React from 'react';
import brand from './brand';

// The product's name, in the product's own colours.
//
// Most products set their name in one ink. One of them picks out the end of it - "OS" of "YardOS" -
// in a blue of its own, which is a fact about that name and not about this page, so it arrives with
// the brand from the server like the name itself. Nothing here knows which product it is drawing.
//
// The accent is described as the end of the word rather than as a number of letters, because that is
// what somebody means when they say "OS in blue", and because a letter count would silently colour
// the wrong part the day a name changes length.

/**
 * The name split into the part drawn in the page's own ink and the part drawn in the brand's colour.
 *
 * The accent applies to the product's own name and to nothing else. Matching on the ending alone
 * was not enough: these headings show a customer's name wherever they have set one, and a customer
 * called "TATA STEEL NOS" would have had two of their letters turned blue because the software
 * underneath happens to end the same way. So the word has to *be* the product's wordmark, which is
 * a thing this can check rather than a thing it has to assume.
 *
 * Exported because it is the whole of the logic worth checking, and checking it through a rendered
 * page would prove less about it than reading the two strings does.
 */
export const wordmarkParts = (word, productName, accent) => {
  const text = typeof word === 'string' ? word : '';
  if (text !== productName) return [text, ''];
  // A word that is nothing but the accent would be drawn entirely in the accent colour, leaving no
  // name in the page's own ink. That is not a two-coloured name, so it is left alone.
  if (!accent || accent.length >= text.length || !text.endsWith(accent)) return [text, ''];
  return [text.slice(0, text.length - accent.length), accent];
};

/**
 * Draws it. `name` overrides the product's own, for the headings that show a customer's name
 * instead; the accent then does not apply, because that name is theirs.
 */
const Wordmark = ({ name = null }) => {
  const word = name ?? brand.wordmark;
  const [plain, tail] = wordmarkParts(word, brand.wordmark, brand.wordmarkAccent);
  if (!tail) return word;
  return (
    <>
      {plain}
      <span style={{ color: brand.wordmarkAccentColor || undefined }}>{tail}</span>
    </>
  );
};

export default Wordmark;
